import React, { useEffect, useState } from 'react';
import { useReducedMotion } from 'framer-motion';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Pagination, Autoplay } from 'swiper';
import 'swiper/css';
import 'swiper/css/pagination';
import heroSlide2 from '../../../images/home/hero-slide-2.png';
import heroSlide3 from '../../../images/home/hero-slide-3.png';
import heroSlide4 from '../../../images/home/hero-slide-4.png';
import { HomeBtn } from './HomePrimitives';
import { initBannerSwiper } from '../../../utils/swiperBannerInit';
import { usePageBanners } from '../../../hooks/usePageBanners';
import { isPrerender } from '../../../seo/siteConfig';
import FlowdoLoader from '../FlowdoLoader/FlowdoLoader';

const HOME_BANNER_SECTION = 'Home Page';

const FALLBACK_SLIDES = [
  {
    id: 'add-a-module',
    title: "Pay for what you use. Skip what you don't.",
    description:
      "Pick the tools you need, skip the ones you don't, and be up and running in minutes — no IT department required.",
    image: heroSlide2,
    alt: 'Add a module — start with Tasks and add Documents, Snap bill, Vault, Forum, or Projects anytime',
    buttonText: 'Get Started',
    buttonTo: '/register',
  },
  {
    id: 'affordable-pricing',
    title: 'Pay as low as $1 per month. 80% less than market rate.',
    description:
      'Our pricing is tailored to support small and medium enterprises. No additional application required to manage your business.',
    image: heroSlide3,
    alt: 'Affordable module pricing — pay only for the flowdo modules you use',
    buttonText: 'Get Started',
    buttonTo: '/register',
  },
  {
    id: 'free-forever',
    title: 'Free forever! No Credit Card.',
    description:
      'Every module starts free. Explore the platform risk-free, and upgrade only when you\'re ready for advanced features.',
    image: heroSlide4,
    alt: 'No credit card required — every flowdo module is free to start',
    buttonText: 'Get Started',
    buttonTo: '/register',
  },
];

const isExternalUrl = (url) => /^https?:\/\//i.test(url || '');

function mapBannerToSlide(banner) {
  return {
    id: banner.id ?? banner.name,
    title: banner.main_heading_text || '',
    description: banner.sub_heading_text || '',
    image: banner.imageDesktopUrl,
    imageMobile: banner.imageMobileUrl,
    alt: banner.name || banner.main_heading_text || 'flowdo banner',
    buttonText: banner.button_text || 'Get Started',
    buttonTo: banner.button_action_url || '/register',
    titleColor: banner.main_heading_text_color,
    descriptionColor: banner.sub_heading_text_color,
  };
}

function HeroCta({ slide }) {
  if (isExternalUrl(slide.buttonTo)) {
    return (
      <a href={slide.buttonTo} className="home-btn home-btn--primary">
        {slide.buttonText}
      </a>
    );
  }
  return <HomeBtn to={slide.buttonTo}>{slide.buttonText}</HomeBtn>;
}

function HeroSlide({ slide, priority = false }) {
  return (
    <div className="home-container home-hero-grid">
      <div className="home-hero-copy">
        {slide.title ? (
          <h2
            className="home-hero-title"
            style={slide.titleColor ? { color: slide.titleColor } : undefined}
          >
            {slide.title}
          </h2>
        ) : null}
        {slide.description ? (
          <p
            className="home-hero-sub"
            style={slide.descriptionColor ? { color: slide.descriptionColor } : undefined}
          >
            {slide.description}
          </p>
        ) : null}
        <div className="home-hero-actions">
          <HeroCta slide={slide} />
        </div>
      </div>

      <div className="home-hero-visual">
        <div className="home-hero-stage">
          <picture>
            {slide.imageMobile ? (
              <source media="(max-width: 768px)" srcSet={slide.imageMobile} />
            ) : null}
            <img
              src={slide.image}
              alt={slide.alt}
              className="home-hero-photo"
              draggable="false"
              fetchPriority={priority ? 'high' : 'auto'}
              decoding={priority ? 'sync' : 'async'}
            />
          </picture>
        </div>
      </div>
    </div>
  );
}

function HeroSpinner() {
  return (
    <div className="home-hero-placeholder">
      <FlowdoLoader label="Loading banner…" />
    </div>
  );
}

function useHeroImageReady(src) {
  const [ready, setReady] = useState(() => !src);

  useEffect(() => {
    if (!src) {
      setReady(true);
      return;
    }

    let cancelled = false;
    const img = new Image();
    const markReady = () => {
      if (!cancelled) setReady(true);
    };
    img.onload = markReady;
    img.onerror = markReady;
    img.src = src;
    if (img.complete) markReady();

    return () => {
      cancelled = true;
    };
  }, [src]);

  return ready;
}

function releaseHomeHeroBoot() {
  if (typeof document === 'undefined') return;
  document.documentElement.classList.remove('home-hero-booting');
}

function HomeHeroCarousel({ slides, reduce }) {
  const [swiperReady, setSwiperReady] = useState(false);
  const imageReady = useHeroImageReady(slides[0]?.image);
  const paged = slides.length > 1;
  const shown = imageReady && (!paged || swiperReady);

  useEffect(() => {
    if (shown) releaseHomeHeroBoot();
  }, [shown]);

  if (!imageReady) {
    return <HeroSpinner />;
  }

  if (!paged) {
    return (
      <div className="home-hero-frame">
        <HeroSlide slide={slides[0]} priority />
      </div>
    );
  }

  return (
    <div className={`home-hero-frame home-hero-frame--paged${swiperReady ? '' : ' is-pending'}`}>
      {!swiperReady && <HeroSpinner />}
      <div
        className="home-hero-swiper-hold"
        aria-hidden={!swiperReady}
        style={
          swiperReady
            ? undefined
            : { position: 'absolute', inset: 0, visibility: 'hidden', pointerEvents: 'none' }
        }
      >
        <Swiper
          modules={[Pagination, Autoplay]}
          pagination={{ clickable: true }}
          loop={false}
          rewind
          initialSlide={0}
          spaceBetween={0}
          slidesPerView={1}
          autoplay={
            reduce ? false : { delay: 6000, disableOnInteraction: false, pauseOnMouseEnter: true }
          }
          className={`home-hero-swiper${swiperReady ? ' is-ready' : ''}`}
          onSwiper={(swiper) => {
            initBannerSwiper(swiper);
            requestAnimationFrame(() => {
              if (swiper.destroyed) return;
              requestAnimationFrame(() => {
                if (!swiper.destroyed) setSwiperReady(true);
              });
            });
          }}
        >
          {slides.map((slide, index) => (
            <SwiperSlide key={slide.id}>
              <HeroSlide slide={slide} priority={index === 0} />
            </SwiperSlide>
          ))}
        </Swiper>
      </div>
    </div>
  );
}

const HomeHero = () => {
  const reduce = useReducedMotion();
  const prerender = isPrerender();
  const { banners, loading, hasDynamicBanner } = usePageBanners(HOME_BANNER_SECTION);
  const [clientReady, setClientReady] = useState(false);

  const slides = hasDynamicBanner
    ? banners.filter((banner) => banner.imageDesktopUrl).map(mapBannerToSlide)
    : FALLBACK_SLIDES;

  useEffect(() => {
    if (prerender) return;
    setClientReady(true);
  }, [prerender]);

  useEffect(() => {
    if (!prerender && !loading && clientReady && slides.length === 0) {
      releaseHomeHeroBoot();
    }
  }, [prerender, loading, clientReady, slides.length]);

  useEffect(() => () => releaseHomeHeroBoot(), []);

  if (prerender || loading || !clientReady) {
    return (
      <section className="home-hero" aria-busy="true">
        <HeroSpinner />
      </section>
    );
  }

  if (slides.length === 0) {
    return null;
  }

  const slideKey = slides.map((slide) => slide.id).join('-');

  return (
    <section className="home-hero">
      <HomeHeroCarousel key={slideKey} slides={slides} reduce={reduce} />
    </section>
  );
};

export default HomeHero;
